import type { ReactNode, JSX } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import { Container, ContainerProps, Heading, VipHeading } from '../../atoms';
import type { CommonProps } from '../../types';

export interface HeadingLayoutProps
    extends CommonProps,
        VariantProps<typeof HeadingLayoutStyles>,
        Pick<ContainerProps, 'maxWidth'> {
    title?: string;
    containerClass?: string;
    description?: string;
    children?: ReactNode;
    invertFontColor?: boolean;
    layout?: 'default' | 'mainBrand' | 'vip';
    heading?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    headingClassName?: string;
}

export const HeadingLayoutStyles = tv({
    slots: {
        root: 'mb-7 md:mb-16',
        container: 'mb-2 px-0 md:mb-8',
        heading: '',
    },
    variants: {
        isVIP: { true: { root: 'selection-ba' } },
        flexHeading: {
            true: {
                heading: 'flex flex-col gap-1',
            },
        },
    },
    defaultVariants: { isVIP: false },
});

export const HeadingLayout = ({
    className,
    title,
    description,
    children,
    isVIP,
    containerClass,
    invertFontColor = false,
    testId = 'heading-layout',
    maxWidth = 'xl',
    heading,
    headingClassName,
    ...props
}: HeadingLayoutProps): JSX.Element => {
    const { root, container, heading: headingStyle } = HeadingLayoutStyles();

    return (
        <div
            className={root({ isVIP, className })}
            {...props}
            data-testid={testId}
        >
            <Container
                className={container({ className: containerClass })}
                maxWidth={maxWidth}
            >
                <div
                    className={headingStyle({
                        flexHeading: !!title && !!description,
                    })}
                >
                    {title &&
                        (isVIP ? (
                            <div className="px-4">
                                <VipHeading
                                    invertFontColor={invertFontColor}
                                    title={title}
                                />
                            </div>
                        ) : (
                            <Heading
                                as={heading ?? 'h2'}
                                className={headingClassName}
                            >
                                {title}
                            </Heading>
                        ))}

                    {description && (
                        <p className="mb-8 text-center">{description}</p>
                    )}
                </div>

                {children}
            </Container>
        </div>
    );
};
